import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, KeyRound, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { BonkerLogo } from "@/components/BonkerLogo";

const rules = [
  { label: "Almeno 8 caratteri", test: (p: string) => p.length >= 8 },
  { label: "Una lettera maiuscola", test: (p: string) => /[A-Z]/.test(p) },
  { label: "Un numero", test: (p: string) => /[0-9]/.test(p) },
];

export default function ChangePasswordPage() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const forced = !!user?.must_change_password;
  const allRulesOk = rules.every((r) => r.test(next));
  const match = next.length > 0 && next === confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allRulesOk) return toast.error("La password non rispetta i requisiti");
    if (!match) return toast.error("Le password non coincidono");
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      await refresh();
      toast.success("Password aggiornata con successo");
      navigate("/cms", { replace: true });
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 relative bonker-noise">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="w-full max-w-md relative z-10"
      >
        <div className="mb-8 flex items-center justify-between">
          <BonkerLogo className="text-2xl" />
          <button
            data-testid="change-password-logout-button"
            onClick={() => {
              logout();
              navigate("/cms/login", { replace: true });
            }}
            className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground hover:text-secondary transition-colors"
          >
            Esci
          </button>
        </div>

        <div className="border border-white/10 bg-card p-8">
          <div className="flex items-center gap-3 mb-2 text-primary">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold">Sicurezza account</span>
          </div>
          <h1 className="font-display font-bold text-3xl tracking-tight mb-2">
            {forced ? "Cambia password" : "Aggiorna password"}
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            {forced
              ? "Per il primo accesso è obbligatorio impostare una nuova password personale."
              : "Imposta una nuova password per il tuo account."}
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
            <PasswordField
              testid="current-password-input"
              label="Password attuale"
              value={current}
              onChange={setCurrent}
            />
            <PasswordField
              testid="new-password-input"
              label="Nuova password"
              value={next}
              onChange={setNext}
            />
            <PasswordField
              testid="confirm-password-input"
              label="Conferma nuova password"
              value={confirm}
              onChange={setConfirm}
            />

            <ul className="space-y-1.5 pt-1" data-testid="password-rules">
              {rules.map((r) => {
                const ok = r.test(next);
                return (
                  <li key={r.label} className="flex items-center gap-2 text-xs">
                    {ok ? (
                      <Check className="h-3.5 w-3.5 text-secondary" />
                    ) : (
                      <X className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <span className={ok ? "text-foreground" : "text-muted-foreground"}>
                      {r.label}
                    </span>
                  </li>
                );
              })}
              <li className="flex items-center gap-2 text-xs">
                {match ? (
                  <Check className="h-3.5 w-3.5 text-secondary" />
                ) : (
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                <span className={match ? "text-foreground" : "text-muted-foreground"}>
                  Le password coincidono
                </span>
              </li>
            </ul>

            <button
              data-testid="change-password-submit-button"
              type="submit"
              disabled={loading}
              className="group w-full bg-primary text-primary-foreground font-bold uppercase tracking-[0.15em] text-sm py-4 flex items-center justify-center gap-2 transition-transform duration-200 hover:-translate-y-[2px] disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <KeyRound className="h-4 w-4" />
                  Salva password
                </>
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

const PasswordField: React.FC<{
  testid: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ testid, label, value, onChange }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
      {label}
    </label>
    <input
      data-testid={testid}
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      placeholder="••••••••"
      className="mt-2 w-full border border-input bg-black/50 py-3 px-4 text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary transition-colors"
    />
  </div>
);
